import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import * as XLSX from 'xlsx';
import {
  Row, Col, Card, Table, Button, Input,
  notification, Modal, Drawer, Select, Typography,
  Space, Tag, Tooltip, DatePicker, Statistic, Form, Spin,
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  DeleteOutlined, FileExcelOutlined, FilterOutlined, EditOutlined,
  ExclamationCircleOutlined, PlusOutlined,
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
  CalendarOutlined, SearchOutlined, ReloadOutlined,
  DollarOutlined, ClockCircleOutlined, FileDoneOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { hasPermission } from '../../components/permissions';
import '../../assets/styles/custom_style.css';
import moment from 'moment';
import ExpendituresForm from './Form';

const { confirm } = Modal;
const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

// Current fiscal year: April–March
function currentFiscalYear() {
  const now = new Date();
  const y = now.getFullYear();
  return now.getMonth() >= 3 ? `${y}-${String(y + 1).slice(-2)}` : `${y - 1}-${String(y).slice(-2)}`;
}

function ExpendituresList() {
  const history = useHistory();

  const [expendituresList, setExpendituresList] = useState([]);
  const [beneficiariesList, setBeneficiariesList] = useState([]);
  const [purposesList, setPurposesList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);

  const [filters, setFilters] = useState({
    beneficiary_type: '',
    purpose_category: '',
    approval_status: '',
    payment_mode: '',
    fiscal_year: '',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: '',
  });

  const [beneficiaryTypes, setBeneficiaryTypes] = useState([]);
  const [purposeCategories, setPurposeCategories] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentExpenditureId, setCurrentExpenditureId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // Summary stats
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Quick filter active label — default 'all' so data shows on first load
  const [activeQuick, setActiveQuick] = useState('all');

  // Reject reason modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectTxnId, setRejectTxnId] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  useEffect(() => {
    // Check if we were navigated here from Reports page with a specific month
    const quickStart = sessionStorage.getItem('exp_quick_start');
    const quickEnd   = sessionStorage.getItem('exp_quick_end');
    const quickLabel = sessionStorage.getItem('exp_quick_label');
    if (quickStart && quickEnd) {
      sessionStorage.removeItem('exp_quick_start');
      sessionStorage.removeItem('exp_quick_end');
      sessionStorage.removeItem('exp_quick_label');
      const drillFilters = { ...filters, start_date: quickStart, end_date: quickEnd, fiscal_year: '' };
      setFilters(drillFilters);
      setActiveQuick('custom');
      notification.info({
        message: `Showing expenditures for ${quickLabel || quickStart + ' – ' + quickEnd}`,
        placement: 'topRight',
        duration: 3,
      });
      getExpendituresList(1, limit, sortField, sortOrder, '', drillFilters);
      loadSummary(drillFilters);
    } else {
      getExpendituresList();
      loadSummary({});
    }
    loadDropdownData();
  }, []); // eslint-disable-line

  // ── Quick date filters ────────────────────────────────────────────────────────
  const applyQuickFilter = (key) => {
    const now = moment();
    let newFilters = { ...filters, start_date: '', end_date: '', fiscal_year: '' };

    if (key === 'all') {
      // no extra filter — show everything
    } else if (key === 'this_month') {
      newFilters.start_date = now.startOf('month').format('YYYY-MM-DD');
      newFilters.end_date   = moment().endOf('month').format('YYYY-MM-DD');
      newFilters.fiscal_year = '';
    } else if (key === 'last_month') {
      const lm = moment().subtract(1, 'month');
      newFilters.start_date = lm.startOf('month').format('YYYY-MM-DD');
      newFilters.end_date   = lm.clone().endOf('month').format('YYYY-MM-DD');
      newFilters.fiscal_year = '';
    } else if (key === 'this_fy') {
      newFilters.fiscal_year = currentFiscalYear();
      newFilters.start_date  = '';
      newFilters.end_date    = '';
    } else if (key === 'this_year') {
      newFilters.start_date = `${now.year()}-01-01`;
      newFilters.end_date   = `${now.year()}-12-31`;
      newFilters.fiscal_year = '';
    } else if (key === 'last_fy') {
      const y = now.month() >= 3 ? now.year() - 1 : now.year() - 2;
      newFilters.fiscal_year = `${y}-${String(y+1).slice(-2)}`;
      newFilters.start_date  = '';
      newFilters.end_date    = '';
    }

    setFilters(newFilters);
    setActiveQuick(key);
    setSearch('');
    getExpendituresList(1, limit, sortField, sortOrder, '', newFilters);
    loadSummary(newFilters);
  };

  // ── Summary stats ─────────────────────────────────────────────────────────────
  const loadSummary = useCallback(async (flt = filters) => {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      if (flt.fiscal_year)                  params.set('fiscal_year', flt.fiscal_year);
      if (flt.start_date && flt.end_date)   { params.set('start_date', flt.start_date); params.set('end_date', flt.end_date); }
      const res = await axios.get(
        `${baseUrl}/expenditures/summary${params.toString() ? `?${params}` : ''}`,
        { headers: authHeaders() }
      );
      if (res.data.status) setSummary(res.data.result);
    } catch (_) {}
    finally { setSummaryLoading(false); }
  }, []); // eslint-disable-line

  const loadDropdownData = async () => {
    try {
      const [benRes, purRes, typesRes, catsRes] = await Promise.all([
        axios.get(`${baseUrl}/beneficiaries/list?limit=all`, { headers: authHeaders() }),
        axios.get(`${baseUrl}/purposes/list?limit=all`, { headers: authHeaders() }),
        axios.get(`${baseUrl}/beneficiary-types/list`, { headers: authHeaders() }),
        axios.get(`${baseUrl}/purpose-categories/list`, { headers: authHeaders() }),
      ]);
      setBeneficiariesList(benRes.data.result || []);
      setPurposesList(purRes.data.result || []);
      if (typesRes.data.status) setBeneficiaryTypes(typesRes.data.result);
      if (catsRes.data.status) setPurposeCategories(catsRes.data.result);
    } catch (err) {
      console.error('Failed to load dropdown data:', err);
    }
  };

  const getExpendituresList = async (
    page = 1, lim = 20, sf = 'id', so = 'desc', srch = '', flt = filters
  ) => {
    try {
      setLoading(true);
      const filterParams = Object.entries(flt)
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');

      const res = await axios.get(
        `${baseUrl}/expenditures/list?page=${page}&limit=${lim}&sortField=${sf}&sortOrder=${so}${srch ? `&search=${srch}` : ''}&${filterParams}`,
        { headers: authHeaders() }
      );
      if (res.data.status) {
        setExpendituresList(res.data.result);
        setPagination({ current: page, pageSize: lim, total: res.data.totalCount });
        setCurrentPage(page);
        setLimit(lim);
        setSortField(sf);
        setSortOrder(so);
      }
    } catch (_) {
      notification.error({ message: 'Failed to fetch expenditures', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  };

  // ── Export Excel ──────────────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    try {
      const filterParams = Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');

      const res = await axios.get(
        `${baseUrl}/expenditures/list?limit=all&sortField=${sortField}&sortOrder=${sortOrder}&${filterParams}`,
        { headers: authHeaders() }
      );
      const rows = res.data.result || [];
      if (!rows.length) { notification.warning({ message: 'No data to export', placement: 'topRight' }); return; }

      const data = rows.map((e, i) => {
        let lineItems = [];
        try { lineItems = e.line_items ? JSON.parse(e.line_items) : []; } catch (_) {}
        let subtotal = 0, taxTotal = 0;
        lineItems.forEach(item => {
          const s = (item.qty || 0) * (item.rate || 0);
          subtotal += s;
          taxTotal += s * (item.taxPct || 0) / 100;
        });
        const grand = lineItems.length ? subtotal + taxTotal : parseFloat(e.amount || 0);

        return {
          'S.No':          i + 1,
          'Transaction ID': e.txn_id,
          'Date':          moment(e.txn_date).format('DD-MM-YYYY'),
          'Fiscal Year':   e.fiscal_year,
          'Beneficiary':   e.beneficiary_name,
          'Beneficiary Type': e.beneficiary_type,
          'Purpose':       e.purpose_name,
          'Purpose Category': e.purpose_category,
          'Amount (₹)':   grand.toFixed(2),
          'Tax (₹)':      taxTotal.toFixed(2),
          'Payment Mode':  e.payment_mode,
          'UTR / Cheque':  e.utr_no || e.cheque_no || '',
          'Status':        e.approval_status,
          'Invoice No.':   e.invoice_no || '',
          'Remarks':       e.remarks || '',
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Expenditures');
      const fy = filters.fiscal_year || 'all';
      XLSX.writeFile(wb, `expenditures_${fy}_${moment().format('DDMMYYYY')}.xlsx`);
      notification.success({ message: `Exported ${rows.length} records`, placement: 'topRight' });
    } catch (err) {
      notification.error({ message: 'Export failed: ' + err.message, placement: 'topRight' });
    }
  };

  // ── Download PDF voucher ──────────────────────────────────────────────────────
  const [downloadingId, setDownloadingId] = useState(null);
  const handleDownloadPdf = async (id, txnId) => {
    setDownloadingId(id);
    try {
      const res = await axios.get(`${baseUrl}/expenditures/generate-invoice/${id}`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      link.download = `voucher-${txnId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      notification.error({ message: 'PDF download failed', placement: 'topRight' });
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Approve ───────────────────────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      await axios.patch(`${baseUrl}/expenditures/approve/${id}`,
        { comments: 'Approved' }, { headers: authHeaders() });
      notification.success({ message: 'Expenditure approved', placement: 'topRight' });
      refresh();
    } catch (_) {
      notification.error({ message: 'Failed to approve', placement: 'topRight' });
    }
  };

  // ── Reject with reason ────────────────────────────────────────────────────────
  const openRejectModal = (id, txnId) => {
    setRejectTargetId(id);
    setRejectTxnId(txnId);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      notification.warning({ message: 'Please enter a reason for rejection', placement: 'topRight' });
      return;
    }
    setRejectLoading(true);
    try {
      await axios.patch(`${baseUrl}/expenditures/reject/${rejectTargetId}`,
        { comments: rejectReason }, { headers: authHeaders() });
      notification.success({ message: 'Expenditure rejected', placement: 'topRight' });
      setRejectModalOpen(false);
      refresh();
    } catch (_) {
      notification.error({ message: 'Failed to reject', placement: 'topRight' });
    } finally {
      setRejectLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${baseUrl}/expenditures/delete/${id}`, { headers: authHeaders() });
      notification.success({ message: 'Deleted successfully', placement: 'topRight' });
      refresh();
    } catch (_) {
      notification.error({ message: 'Delete failed', placement: 'topRight' });
    }
  };

  const showDeleteConfirm = (id, txnId) => {
    confirm({
      title: 'Delete Expenditure',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: `Delete "${txnId}"? This cannot be undone.`,
      okText: 'Delete', okType: 'danger', cancelText: 'Cancel',
      onOk: () => handleDelete(id),
    });
  };

  const showApproveConfirm = (id, txnId) => {
    confirm({
      title: 'Approve Expenditure',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      content: `Approve "${txnId}"?`,
      okText: 'Approve', okType: 'primary',
      onOk: () => handleApprove(id),
    });
  };

  const refresh = () => {
    getExpendituresList(currentPage, limit, sortField, sortOrder, search, filters);
    loadSummary(filters);
  };

  const showDrawer = (id = null) => {
    setCurrentExpenditureId(id);
    setIsUpdateMode(!!id);
    setDrawerVisible(true);
  };

  const closeDrawer = () => { setDrawerVisible(false); setCurrentExpenditureId(null); };

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  const handleSearch = () => {
    getExpendituresList(1, limit, sortField, sortOrder, search, filters);
    loadSummary(filters);
  };

  const handleResetFilters = () => {
    const reset = {
      beneficiary_type: '', purpose_category: '', approval_status: '',
      payment_mode: '', fiscal_year: '',
      start_date: '', end_date: '', min_amount: '', max_amount: '',
    };
    setFilters(reset);
    setActiveQuick('all');
    setSearch('');
    getExpendituresList(1, limit, sortField, sortOrder, '', reset);
    loadSummary(reset);
  };

  const handleTableChange = (pag, _, sorter) => {
    const so = sorter.order === 'ascend' ? 'asc' : 'desc';
    getExpendituresList(pag.current, pag.pageSize, sorter.field || sortField, so, search, filters);
  };

  // ── Columns ───────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: '#', dataIndex: 'key', key: 'key', fixed: 'left', width: 55, align: 'center',
    },
    {
      title: 'Transaction ID', dataIndex: 'txn_id', key: 'txn_id', fixed: 'left', width: 150,
      render: t => <Text strong style={{ color: '#7a0000', fontFamily: 'monospace', fontSize: 12 }}>{t}</Text>,
    },
    {
      title: 'Date', dataIndex: 'txn_date', key: 'txn_date', width: 110, sorter: true,
      render: d => moment(d).format('DD MMM YYYY'),
    },
    {
      title: 'Beneficiary', key: 'beneficiary', width: 190,
      render: (_, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{r.beneficiary_name}</Text>
          <br /><Tag style={{ fontSize: 10, background: '#eef2f8', color: '#7a0000', border: 'none', marginTop: 3 }}>{r.beneficiary_type}</Tag>
        </div>
      ),
    },
    {
      title: 'Purpose', key: 'purpose', width: 170,
      render: (_, r) => (
        <div>
          <Text>{r.purpose_name}</Text>
          <br /><Text type="secondary" style={{ fontSize: 11 }}>{r.purpose_category}</Text>
        </div>
      ),
    },
    {
      title: 'Amount', key: 'amount', width: 150, sorter: true,
      render: (_, r) => {
        let lineItems = [];
        try { lineItems = r.line_items ? JSON.parse(r.line_items) : []; } catch (_) {}
        if (lineItems.length > 0) {
          let sub = 0, tax = 0;
          lineItems.forEach(it => { const s = (it.qty||0)*(it.rate||0); sub+=s; tax+=s*(it.taxPct||0)/100; });
          return (
            <div>
              <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
                ₹{(sub+tax).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Text>
              <div><Tag style={{ fontSize: 10, background: '#eef2f8', color: '#7a0000', border: 'none' }}>{lineItems.length} item{lineItems.length>1?'s':''}</Tag></div>
            </div>
          );
        }
        return (
          <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
            ₹{Number(r.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </Text>
        );
      },
    },
    {
      title: 'Mode', dataIndex: 'payment_mode', key: 'payment_mode', width: 100,
      render: m => <Tag style={{ background: '#f1f5f9', color: '#475569', border: 'none', fontWeight: 600, fontSize: 11 }}>{m}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'approval_status', key: 'approval_status', width: 110,
      render: s => {
        const cfg = {
          Pending:   { color: 'warning',   icon: <ClockCircleOutlined /> },
          Approved:  { color: 'success',   icon: <CheckCircleOutlined /> },
          Rejected:  { color: 'error',     icon: <CloseCircleOutlined /> },
          Cancelled: { color: 'default',   icon: <CloseCircleOutlined /> },
        };
        return <Tag color={cfg[s]?.color} icon={cfg[s]?.icon}>{s}</Tag>;
      },
    },
    {
      title: 'FY', dataIndex: 'fiscal_year', key: 'fiscal_year', width: 85, align: 'center',
    },
    {
      title: 'Actions', key: 'action', fixed: 'right', width: 210, align: 'center',
      render: (_, r) => (
        <Space size={3}>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => history.push(`/expenditures/view/${r.id}`)} /></Tooltip>
          <Tooltip title="Download PDF Voucher">
            <Button size="small" icon={<FilePdfOutlined />}
              loading={downloadingId === r.id}
              onClick={() => handleDownloadPdf(r.id, r.txn_id)}
              style={{ color: '#722ed1', borderColor: '#722ed1' }}
            />
          </Tooltip>
          {hasPermission('/expenditures', 'editPermission') && (
            <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => showDrawer(r.id)} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }} /></Tooltip>
          )}
          {r.approval_status === 'Pending' && hasPermission('/expenditures', 'editPermission') && (
            <>
              <Tooltip title="Approve">
                <Button size="small" icon={<CheckCircleOutlined />}
                  style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                  onClick={() => showApproveConfirm(r.id, r.txn_id)} />
              </Tooltip>
              <Tooltip title="Reject">
                <Button size="small" danger icon={<CloseCircleOutlined />}
                  onClick={() => openRejectModal(r.id, r.txn_id)} />
              </Tooltip>
            </>
          )}
          {hasPermission('/expenditures', 'deletePermission') && (
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => showDeleteConfirm(r.id, r.txn_id)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const fmt = (v) => '₹' + Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  return (
    <div className="tabled">
      {/* ── Quick Filters ── */}
      <div style={{ marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#888', marginRight: 4 }}>Quick view:</span>
        {[
          { key: 'all',        label: 'All Time' },
          { key: 'this_month', label: 'This Month' },
          { key: 'last_month', label: 'Last Month' },
          { key: 'this_fy',    label: `FY ${currentFiscalYear()}` },
          { key: 'last_fy',    label: 'Last FY' },
          { key: 'this_year',  label: `Year ${new Date().getFullYear()}` },
        ].map(({ key, label }) => (
          <Button
            key={key}
            size="small"
            type={activeQuick === key ? 'primary' : 'default'}
            onClick={() => applyQuickFilter(key)}
            style={activeQuick === key
              ? { background: '#7a0000', borderColor: '#7a0000', fontWeight: 600 }
              : { fontWeight: 500 }
            }
          >
            {label}
          </Button>
        ))}
        <Button
          size="small"
          type={activeQuick === 'custom' ? 'primary' : 'default'}
          onClick={() => { setActiveQuick('custom'); setShowFilter(true); }}
          style={activeQuick === 'custom'
            ? { background: '#722ed1', borderColor: '#722ed1', fontWeight: 600 }
            : { fontWeight: 500 }
          }
        >
          Custom Filter
        </Button>
      </div>

      {/* ── Summary Cards ── */}
      {summaryLoading ? (
        <div style={{ textAlign: 'center', padding: '20px 0 12px' }}><Spin /></div>
      ) : summary && (
        <Row gutter={[14, 14]} style={{ marginBottom: 18 }}>
          {[
            { label: 'This Month (Approved)', value: fmt(summary.this_month_amount), accent: '#7a0000', bg: '#eef2f8', icon: <CalendarOutlined /> },
            { label: `Approved — ${filters.fiscal_year || (filters.start_date ? `${moment(filters.start_date).format('DD MMM')} – ${moment(filters.end_date).format('DD MMM YYYY')}` : 'All')}`, value: fmt(summary.approved_amount), accent: '#15803d', bg: '#dcfce7', icon: <FileDoneOutlined /> },
            { label: `Pending (${summary.pending_count})`, value: fmt(summary.pending_amount), accent: '#d97706', bg: '#fef3c7', icon: <ClockCircleOutlined /> },
            { label: `Total (${summary.total_count})`, value: fmt(summary.total_amount), accent: '#7c3aed', bg: '#ede9fe', icon: <DollarOutlined /> },
          ].map((sc, i) => (
            <Col key={i} xs={12} sm={6}>
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', borderTop: `3px solid ${sc.accent}`, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, lineHeight: 1.4 }}>{sc.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: sc.accent }}>{sc.value}</div>
                  </div>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: sc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: sc.accent, flexShrink: 0 }}>
                    {sc.icon}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* ── Main Table Card ── */}
      <Row gutter={[24, 0]}>
        <Col xs={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 15 }}>Expenditures Management</span>}
            extra={
              <Space>
                {hasPermission('/expenditures', 'createPermission') && (
                  <Button icon={<PlusOutlined />} onClick={() => showDrawer()}
                    style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', fontWeight: 600, borderRadius: 8 }}>
                    Add Expenditure
                  </Button>
                )}
                <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}
                  style={{ borderRadius: 8, fontWeight: 500 }}>
                  Export Excel
                </Button>
              </Space>
            }
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                loading={loading}
                scroll={{ x: 'max-content' }}
                bordered
                size="middle"
                title={() => (
                  <div>
                    <Row gutter={[12, 8]} align="middle" style={{ marginBottom: 8 }}>
                      <Col>
                        <Button icon={<FilterOutlined />} onClick={() => setShowFilter(!showFilter)}>
                          {showFilter ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                      </Col>
                      <Col flex="1">
                        <Input
                          placeholder="Search by transaction ID, beneficiary..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ maxWidth: 320 }}
                          suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
                        />
                      </Col>
                    </Row>

                    {showFilter && (
                      <div style={{ padding: 14, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                        <Row gutter={[10, 10]}>
                          <Col span={4}>
                            <Select placeholder="Fiscal Year" value={filters.fiscal_year || undefined}
                              onChange={v => handleFilterChange('fiscal_year', v || '')}
                              style={{ width: '100%' }} allowClear>
                              {['2022-23','2023-24','2024-25','2025-26','2026-27'].map(fy => (
                                <Option key={fy} value={fy}>{fy}</Option>
                              ))}
                            </Select>
                          </Col>
                          <Col span={5}>
                            <RangePicker
                              format="DD-MM-YYYY"
                              style={{ width: '100%' }}
                              value={[
                                filters.start_date ? moment(filters.start_date) : null,
                                filters.end_date   ? moment(filters.end_date)   : null,
                              ]}
                              onChange={dates => {
                                handleFilterChange('start_date', dates?.[0]?.format('YYYY-MM-DD') || '');
                                handleFilterChange('end_date',   dates?.[1]?.format('YYYY-MM-DD') || '');
                              }}
                            />
                          </Col>
                          <Col span={4}>
                            <Select placeholder="Beneficiary Type"
                              value={filters.beneficiary_type || undefined}
                              onChange={v => handleFilterChange('beneficiary_type', v || '')}
                              style={{ width: '100%' }} allowClear>
                              {beneficiaryTypes.map(t => <Option key={t.id} value={t.name}>{t.name}</Option>)}
                            </Select>
                          </Col>
                          <Col span={4}>
                            <Select placeholder="Purpose Category"
                              value={filters.purpose_category || undefined}
                              onChange={v => handleFilterChange('purpose_category', v || '')}
                              style={{ width: '100%' }} allowClear>
                              {purposeCategories.map(c => <Option key={c.id} value={c.name}>{c.name.replace(/_/g,' ')}</Option>)}
                            </Select>
                          </Col>
                          <Col span={3}>
                            <Select placeholder="Status" value={filters.approval_status || undefined}
                              onChange={v => handleFilterChange('approval_status', v || '')}
                              style={{ width: '100%' }} allowClear>
                              <Option value="Pending">Pending</Option>
                              <Option value="Approved">Approved</Option>
                              <Option value="Rejected">Rejected</Option>
                              <Option value="Cancelled">Cancelled</Option>
                            </Select>
                          </Col>
                          <Col span={4}>
                            <Select placeholder="Payment Mode" value={filters.payment_mode || undefined}
                              onChange={v => handleFilterChange('payment_mode', v || '')}
                              style={{ width: '100%' }} allowClear>
                              {['Cheque','NEFT','RTGS','UPI','Cash','Other'].map(m => <Option key={m} value={m}>{m}</Option>)}
                            </Select>
                          </Col>
                          <Col span={3}>
                            <Input placeholder="Min ₹" value={filters.min_amount}
                              onChange={e => handleFilterChange('min_amount', e.target.value)} type="number" />
                          </Col>
                          <Col span={3}>
                            <Input placeholder="Max ₹" value={filters.max_amount}
                              onChange={e => handleFilterChange('max_amount', e.target.value)} type="number" />
                          </Col>
                          <Col>
                            <Space>
                              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}
                                style={{ background: '#7a0000', borderColor: '#7a0000' }}>Search</Button>
                              <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>Reset</Button>
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </div>
                )}
                dataSource={expendituresList.map((e, i) => ({
                  key: (limit * (currentPage - 1)) + i + 1, ...e,
                }))}
                pagination={{
                  ...pagination,
                  showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100'],
                }}
                onChange={handleTableChange}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Form Drawer ── */}
      <Drawer
        title={<span style={{ fontWeight: 700, color: '#7a0000', fontSize: 15 }}>{isUpdateMode ? 'Update Expenditure' : 'Add Expenditure'}</span>}
        width="88vw"
        open={drawerVisible}
        onClose={closeDrawer}
        destroyOnClose
      >
        <ExpendituresForm
          expenditureId={currentExpenditureId}
          onRefreshData={() => { closeDrawer(); refresh(); }}
          beneficiariesList={beneficiariesList}
          purposesList={purposesList}
        />
      </Drawer>

      {/* ── Reject Reason Modal ── */}
      <Modal
        title={<><CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />Reject Expenditure — {rejectTxnId}</>}
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true, loading: rejectLoading, disabled: !rejectReason.trim() }}
        width={420}
      >
        <Form layout="vertical">
          <Form.Item label="Reason for Rejection" required>
            <Input.TextArea
              rows={3}
              placeholder="e.g. Invoice missing, amount exceeds budget..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ExpendituresList;
